import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuildingAPI, FloorAPI, resolveAsset } from "../../../services/api";
import { getUserFromToken } from "../../../services/authService";
import { ReactComponent as MagnifyingGlassIcon } from "../../../assets/icons/dashboard/magnifyingglass.svg";

const buildingsMock = [
  {
    id: "HPT-001",
    name: "อาคารเฉลิมพระบารมี โรงพยาบาล..",
    address: "เลขที่ 110 ถนนกัลยาณี ตำบลสุเทพ อ.เมือง เชียงใหม่ 50200",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRm2_M9IwFl9lbmELWVlLzX_T-P1_3MKYFTKw&s",
    status: "published",
  },
  {
    id: "HPT-002",
    name: "อาคารสุริโยโน",
    address: "Suthep, 110 ถนนนิมมานฯ, เชียงใหม่ 50200",
    img: "https://w2.med.cmu.ac.th/suandok-variety/wp-content/uploads/2021/01/141008846_904092800406350_2625982473013800007_n-1-scaled-1200x630.jpg",
    status: "published",
  },
  {
    id: "HPT-003",
    name: "อาคารบุญสม มาร์ติน",
    address: "ตำบลสุเทพ อำเภอเมืองเชียงใหม่ เชียงใหม่ 50200",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS2Q6GTuRS52QH2P9_h0o-SaPcGbayiF1sWMQ&s",
    status: "published",
  },
];

const buildingsMock2 = [
  {
    id: "HPT-004",
    name: "ศูนย์ศรีพัฒน์ คณะแพทยศาสตร์",
    address:
      "110 392 ถนนศิริมังคลาจารย์ ตำบลศรีภูมิ อำเภอเมืองเชียงใหม่ เชียงใหม่ 50200",
    img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRCrYSd4E4WUyxkiyrnURkFoK3n7T6m8l46Og&s0",
    status: "draft",
  },
];

export default function BuildingSetting() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = getUserFromToken();
  const canAddBuilding = user?.role === "super_admin";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [serverBuildings, setServerBuildings] = useState(null);
  const [serverTotal, setServerTotal] = useState(null);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [floorModalBuilding, setFloorModalBuilding] = useState(null);
  const [floorModalName, setFloorModalName] = useState("");
  const [floorModalImage, setFloorModalImage] = useState(null);
  const [floorModalLevel, setFloorModalLevel] = useState(1);
  const [floorModalLoading, setFloorModalLoading] = useState(false);
  const [floorModalSaving, setFloorModalSaving] = useState(false);
  const [floorModalError, setFloorModalError] = useState("");
  const [createdFloorId, setCreatedFloorId] = useState(null);
  const itemsPerPage = 3;

  const handleSearchChange = (val) => {
    setQ(val);
    setPage(1);
  };

  useEffect(() => {
    const params = { page, limit: itemsPerPage, pageSize: itemsPerPage };
    if (q.trim()) {
      params.q = q.trim();
    }

    BuildingAPI.list(params)
      .then((r) => {
        const list = Array.isArray(r) ? r : r?.items || r?.buildings || [];
        setServerBuildings(list);
        setServerTotal(Array.isArray(r) ? list.length : r?.total ?? list.length);
        setServerTotalPages(
          Array.isArray(r)
            ? 1
            : r?.totalPages ||
            Math.ceil(
              (r?.total || list.length || 0) /
              (r?.pageSize || r?.limit || itemsPerPage)
            ) ||
            1
        );
      })
      .catch(() => {
        setServerBuildings(null);
        setServerTotal(null);
        setServerTotalPages(1);
      });
  }, [page, q]);

  // Fall back to mock data if backend is not available
  const usingServerPagination = serverBuildings !== null;
  const sourcePublished = buildingsMock;
  const sourceDraft = buildingsMock2;

  const combined = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = [...sourcePublished, ...sourceDraft];
    if (!s) return all;
    return all.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(s) ||
        String(b.code || b.id || "").toLowerCase().includes(s)
    );
  }, [q, sourcePublished, sourceDraft]);

  const totalItems = usingServerPagination ? serverTotal || 0 : combined.length;
  const totalPages = usingServerPagination
    ? Math.max(serverTotalPages, 1)
    : Math.max(Math.ceil(combined.length / itemsPerPage), 1);

  const paginatedItems = useMemo(() => {
    if (usingServerPagination) return serverBuildings;
    const start = (page - 1) * itemsPerPage;
    return combined.slice(start, start + itemsPerPage);
  }, [combined, page, serverBuildings, usingServerPagination]);

  const openAddFloorModal = async (building) => {
    setFloorModalBuilding(building);
    setFloorModalName("");
    setFloorModalImage(null);
    setFloorModalLevel(1);
    setFloorModalError("");
    setCreatedFloorId(null);
    setFloorModalLoading(true);

    try {
      const floors = await FloorAPI.listByBuilding(building.id);
      const list = Array.isArray(floors) ? floors : floors?.items || floors?.floors || [];
      const maxLevel = list.reduce((max, floor, index) => {
        const level = Number(floor.level ?? floor.floor ?? index + 1);
        return Number.isFinite(level) ? Math.max(max, level) : max;
      }, 0);
      const nextLevel = maxLevel + 1;
      setFloorModalLevel(nextLevel);
      setFloorModalName(t("admin.addBuilding.floorNum", { num: nextLevel }).replace("- ", ""));
    } catch (err) {
      setFloorModalLevel(1);
      setFloorModalName(t("admin.addBuilding.floorNum", { num: 1 }).replace("- ", ""));
      setFloorModalError(err?.message || t("admin.buildingSetting.loadFloorsFailed", "โหลดข้อมูลชั้นไม่สำเร็จ"));
    } finally {
      setFloorModalLoading(false);
    }
  };

  const closeAddFloorModal = () => {
    if (floorModalSaving) return;
    setFloorModalBuilding(null);
    setFloorModalName("");
    setFloorModalImage(null);
    setFloorModalError("");
    setCreatedFloorId(null);
  };

  const saveNewFloor = async () => {
    if (!floorModalBuilding) return;

    setFloorModalSaving(true);
    setFloorModalError("");
    setCreatedFloorId(null);

    try {
      const floor = await FloorAPI.create(floorModalBuilding.id, {
        level: floorModalLevel,
        name: floorModalName || t("admin.addBuilding.floorNum", { num: floorModalLevel }).replace("- ", ""),
        image: floorModalImage || null,
      });
      setCreatedFloorId(floor?.id || null);
    } catch (err) {
      setFloorModalError(err?.message || t("admin.buildingSetting.addFloorFailed", "เพิ่มชั้นไม่สำเร็จ"));
    } finally {
      setFloorModalSaving(false);
    }
  };

  return (
    <div className="font-sans min-h-[calc(100vh-96px)] flex flex-col">
      {/* PAGE TITLE */}
      <div className="mb-4">
        <div className="flex items-center gap-2 text-slate-900">
          <h1 className="text-xl p-1 font-semibold">{t("admin.buildingSetting.settingHeader")}</h1>
        </div>
        <p className="text-sm p-1 font-base text-slate-600">{t("admin.buildingSetting.totalBuildings", { count: totalItems })}</p>
      </div>

      {/* BUILDINGS LIST */}
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="font-semibold text-lg text-slate-900">
            {t("admin.dashboard.allBuildings")}
          </div>

          <div className="flex items-center gap-3">
            {canAddBuilding && (
              <button
                onClick={() => navigate("/admin/buildings/add")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-4 py-2 text-sm"
              >
                {t("admin.buildingSetting.addBuilding")}
              </button>
            )}
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 min-w-[220px] sm:min-w-[320px] bg-white">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-500" />
              <input
                className="w-full outline-none font-base text-sm placeholder:text-slate-400"
                value={q}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t("common.search")}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          {paginatedItems.map((b) => {
            const isDraft = b.status === "draft";
            return (
              <div
                key={b.id}
                className="w-[300px] h-[400px] border border-slate-200 rounded-lg overflow-hidden bg-white flex flex-col"
              >
                {/* Building thumbnail */}
                <button
                  type="button"
                  onClick={() => navigate(`/admin/listallbuilding?buildingId=${encodeURIComponent(b.id)}`)}
                  className="h-[200px] rounded-xl overflow-hidden m-2 relative text-left"
                >
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${
                        resolveAsset(
                          (b.name?.toLowerCase().includes("s1") || String(b.code || b.id || "").toLowerCase().includes("s1"))
                            ? "https://asset.oou.cmu.ac.th/wp-content/uploads/2025/06/19429952_1374719025916362_5971276997021923822_n.jpg"
                            : (b.imageUrl || b.img)
                        ) || "https://cdn.chiangmainews.co.th/wp-content/uploads/2021/10/20105616/2018-10_f66c5246b37fdec.jpg"
                      })`
                    }}
                  >
                    <div
                      className={`absolute top-2.5 right-2.5 border text-white text-xs font-semibold px-3.5 py-1.5 rounded-lg ${isDraft
                        ? "bg-orange-700/70 border-orange-500"
                        : "bg-emerald-700/70 border-green-500"
                        }`}
                    >
                      {isDraft ? t("admin.buildingSetting.draft") : t("admin.buildingSetting.published")}
                    </div>
                  </div>
                </button>

                {/* Building info */}
                <div className="m-3 flex flex-col flex-1">
                  <div className="m-1 -mt-1 text-xs text-slate-400">
                    # &nbsp;ID: {b.code || b.id}
                  </div>

                  <div className="mt-2 m-1 font-semibold text-slate-900">
                    {b.name}
                  </div>

                  <div className="-mt-1 m-1 text-xs text-slate-600 leading-relaxed min-h-[34px]">
                    {b.address}
                  </div>

                  <button
                    onClick={() => navigate(`/admin2?buildingId=${encodeURIComponent(b.id)}`)}
                    className="mt-auto w-full bg-blue-600 hover:bg-blue-600 text-white font-semibold rounded-lg py-2 text-sm"
                  >
                    {t("admin.dashboard.buildingSettings")}
                  </button>
                  <button
                    onClick={() => openAddFloorModal(b)}
                    className="mt-2 w-full border border-blue-100 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg py-2 text-sm"
                  >
                    {t("admin.buildingSetting.addFloor", "เพิ่มชั้น")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-5">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="bg-white rounded-xl px-3 py-1.5 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ‹
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              const isCurrent = p === page;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={
                    "rounded-xl px-2 py-1.5 text-sm font-semibold border " +
                    (isCurrent
                      ? "border-blue-600 bg-blue-600 text-white font-semibold"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  {String(p).padStart(2, "0")}
                </button>
              );
            })}

            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="bg-white rounded-xl px-3 py-1.5 font-black text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ›
            </button>
          </div>
        )}
      </section>

      {floorModalBuilding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-[460px] rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
              <div className="min-w-0">
                <div className="text-lg font-black text-slate-900">
                  {createdFloorId
                    ? t("admin.buildingSetting.addFloorSuccess", "เพิ่มชั้นสำเร็จ")
                    : t("admin.buildingSetting.addFloor", "เพิ่มชั้น")}
                </div>
                <div className="mt-1 truncate text-sm font-semibold text-slate-500">
                  {floorModalBuilding.name}
                </div>
              </div>
              <button
                onClick={closeAddFloorModal}
                disabled={floorModalSaving}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-black text-slate-500 disabled:opacity-50"
              >
                {t("common.close", "ปิด")}
              </button>
            </div>

            {createdFloorId ? (
              <div className="py-6">
                <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
                  {t("admin.buildingSetting.addFloorSuccessDesc", "สร้างชั้นใหม่เรียบร้อยแล้ว สามารถไปตั้งค่าแผนที่ของชั้นนี้ต่อได้")}
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={closeAddFloorModal}
                    className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-black text-slate-600"
                  >
                    {t("common.close", "ปิด")}
                  </button>
                  <button
                    onClick={() =>
                      navigate(
                        `/admin2?buildingId=${encodeURIComponent(floorModalBuilding.id)}&floorId=${encodeURIComponent(createdFloorId)}`
                      )
                    }
                    className="flex-1 rounded-xl bg-blue-600 py-2.5 text-sm font-black text-white"
                  >
                    {t("admin.addBuilding.goToMapEditor")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="pt-4">
                {floorModalLoading ? (
                  <div className="rounded-xl bg-slate-50 px-4 py-8 text-center text-sm font-bold text-slate-400">
                    {t("admin.addBuilding.loadingFloors", "กำลังโหลดชั้น...")}
                  </div>
                ) : (
                  <>
                    <div className="grid gap-3">
                      <label className="block">
                        <span className="text-xs font-black text-slate-500">
                          {t("admin.buildingSetting.floorLevel", "ชั้นที่")}
                        </span>
                        <input
                          type="number"
                          min="1"
                          value={floorModalLevel}
                          onChange={(e) => setFloorModalLevel(Number(e.target.value) || 1)}
                          className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-400"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs font-black text-slate-500">
                          {t("admin.buildingSetting.floorName", "ชื่อชั้น")}
                        </span>
                        <input
                          value={floorModalName}
                          onChange={(e) => setFloorModalName(e.target.value)}
                          placeholder={t("admin.addBuilding.enterFloorImageName")}
                          className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm font-bold outline-none focus:border-blue-400"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs font-black text-slate-500">
                          {t("admin.addBuilding.floorMapImageTitle", { floor: floorModalLevel })}
                        </span>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => setFloorModalImage(e.target.files?.[0] || null)}
                          className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:font-black file:text-blue-600"
                        />
                      </label>
                    </div>

                    {floorModalError && (
                      <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm font-bold text-red-600">
                        {floorModalError}
                      </div>
                    )}

                    <div className="mt-5 flex gap-2">
                      <button
                        onClick={closeAddFloorModal}
                        disabled={floorModalSaving}
                        className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-black text-slate-600 disabled:opacity-50"
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        onClick={saveNewFloor}
                        disabled={floorModalSaving}
                        className="flex-1 rounded-xl bg-blue-600 py-2.5 text-sm font-black text-white disabled:opacity-50"
                      >
                        {floorModalSaving ? t("common.saving") : t("admin.buildingSetting.addFloor", "เพิ่มชั้น")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Footer */}
      <div className="mt-auto text-left border border-slate-200 rounded-lg p-4 bg-white text-xs text-slate-500">
        {t("admin.feedbackDetail.copyright")}
      </div>
    </div>
  );
}
